import type { SomeObject } from '@nzyme/types/Object.js';
import { defineContext, injectContext, provideContext } from '@nzyme/vue-utils/context.js';
import { defineProp } from '@nzyme/vue-utils/defineProp.js';
import { defineSlots } from '@nzyme/vue-utils/slots.js';
import type { Slots } from '@nzyme/vue-utils/slots.js';
import type { ComponentPropsWithEmits } from '@nzyme/vue-utils/types.js';
import type { RequiredKeysOf } from 'type-fest';
import { defineComponent, h } from 'vue';
import type { Component } from 'vue';

export type LayoutSlots<P = undefined> = Slots<{
    title: P;
    subtitle: P;
    default: P;
    footer: P;
}>;

// oxlint-disable-next-line typescript/no-explicit-any
export type Layout<P = any> = Component<P, any, any, any, any, any, LayoutSlots>;

export interface LayoutContext {
    layout: Layout;
    props?: object | null | undefined;
}

export const LayoutContext = defineContext<LayoutContext>('Layout');

export function provideLayout<C extends Layout>(layout: C, props: ComponentPropsWithEmits<C>): LayoutContext;
export function provideLayout<C extends Layout>(
    layout: RequiredKeysOf<ComponentPropsWithEmits<C>> extends never ? C : never,
    props?: ComponentPropsWithEmits<C>,
): LayoutContext;
export function provideLayout(layout: Layout, props?: object | null): LayoutContext {
    return provideContext(LayoutContext, { layout, props });
}

export const LayoutProvider = defineComponent({
    name: 'LayoutProvider',
    props: {
        layout: defineProp<Layout<SomeObject>>({ required: true }),
    },
    setup(props, { slots }) {
        provideContext(LayoutContext, props);
        return () => slots.default?.();
    },
});

export default defineComponent({
    name: 'Layout',
    slots: defineSlots<LayoutSlots>(),
    setup(_props, { slots }) {
        const ctx = injectContext(LayoutContext);
        return () => h(ctx.layout, ctx.props, slots);
    },
});
